import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useConfig from "../../demos/hooks/useConfig";
import {
  useDistanceQueryStore,
  DistanceHistoryEntry,
} from "./stores/distanceQueryStore";
import DistanceQueryForm, {
  DistanceQueryPrefill,
  METRIC_LABELS,
} from "./DistanceQueryForm";

const RulerIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="w-6 h-6"
  >
    <path d="M21.3 8.7 8.7 21.3a1 1 0 0 1-1.4 0l-4.6-4.6a1 1 0 0 1 0-1.4L15.3 2.7a1 1 0 0 1 1.4 0l4.6 4.6a1 1 0 0 1 0 1.4Z" />
    <path d="m7.5 10.5 2 2" />
    <path d="m10.5 7.5 2 2" />
    <path d="m13.5 4.5 2 2" />
    <path d="m4.5 13.5 2 2" />
  </svg>
);

const BackIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 12H5" />
    <path d="m12 19-7-7 7-7" />
  </svg>
);

const formatTime = (ts: number) => {
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return "";
  }
};

const CopyIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to legacy path */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

// The three values the user can copy from a history entry.
const COPY_OPTIONS: {
  key: string;
  label: string;
  value: (e: DistanceHistoryEntry) => string;
}[] = [
  { key: "distance", label: "Copy distance", value: (e) => `${e.distance} ${e.unit}`.trim() },
  { key: "from", label: "Copy “from” bay", value: (e) => e.from },
  { key: "to", label: "Copy “to” bay", value: (e) => e.to },
];

const HistoryRow: React.FC<{
  entry: DistanceHistoryEntry;
  onReplay: (e: DistanceHistoryEntry) => void;
}> = ({ entry, onReplay }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async (value: string) => {
    const ok = await copyToClipboard(value);
    setMenuOpen(false);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    }
  };

  return (
    <div className="px-5 py-3 hover:bg-slate-50 transition-colors flex items-center justify-between gap-3">
      <button
        type="button"
        onClick={() => onReplay(entry)}
        className="flex-1 min-w-0 text-left"
        title="Re-run this query"
      >
        <div className="text-sm font-medium text-slate-800 truncate">
          {entry.from} → {entry.to}
        </div>
        <div className="text-xs text-slate-500 truncate">
          {entry.planLabel} · {METRIC_LABELS[entry.metric] || entry.metric} ·{" "}
          {formatTime(entry.at)}
        </div>
      </button>

      <div className="text-right flex-shrink-0">
        <span className="text-base font-semibold text-slate-800">{entry.distance}</span>
        <span className="text-xs text-slate-500 ml-1">{entry.unit}</span>
      </div>

      <div className="relative flex-shrink-0">
        <button
          type="button"
          onClick={() => setMenuOpen((o) => !o)}
          className={`p-1.5 rounded hover:bg-slate-200 transition-colors ${
            copied ? "text-green-600" : "text-slate-500 hover:text-primary-600"
          }`}
          title="Copy value…"
          aria-label="Copy value"
        >
          {copied ? <CheckIcon /> : <CopyIcon />}
        </button>

        {menuOpen && (
          <>
            {/* click-away backdrop */}
            <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
            <div className="absolute right-0 mt-1 w-52 bg-white border border-slate-200 rounded-md shadow-lg z-20 py-1">
              {COPY_OPTIONS.map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  onClick={() => handleCopy(opt.value(entry))}
                  className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center justify-between gap-3"
                >
                  <span className="flex-shrink-0">{opt.label}</span>
                  <span className="text-xs text-slate-400 truncate">{opt.value(entry)}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const DistanceQueryPage: React.FC = () => {
  const navigate = useNavigate();
  const { demoInfo } = useConfig();
  const { history, addHistory, clearHistory } = useDistanceQueryStore();

  const [prefill, setPrefill] = useState<DistanceQueryPrefill | undefined>(undefined);

  useEffect(() => {
    document.title = "Distance Query";
  }, []);

  const handleBack = () => {
    // Prefer browser back (returns to the demo the user came from);
    // fall back to the active demo route on a direct/deep load.
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(demoInfo?.route || "/");
    }
  };

  const handleReplay = (entry: (typeof history)[number]) => {
    setPrefill({
      from: entry.from,
      to: entry.to,
      metric: entry.metric,
      nonce: Date.now(),
    });
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 bg-primary-500 text-white shadow">
        <div className="flex items-center gap-3">
          <RulerIcon />
          <div>
            <h1 className="text-lg font-semibold leading-tight">Distance Query</h1>
            <p className="text-xs text-white/80">
              Bay-to-bay distance lookup (walking &amp; direct)
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={handleBack}
          className="flex items-center gap-1.5 text-sm bg-white/15 hover:bg-white/25 rounded px-3 py-1.5"
          title="Back to the demo"
        >
          <BackIcon />
          Back to demo
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 flex flex-col md:flex-row gap-6 p-6 max-w-6xl w-full mx-auto">
        {/* Query form */}
        <div className="w-full md:w-[420px] flex-shrink-0">
          <div className="bg-white rounded-lg shadow border border-slate-200 p-5">
            <DistanceQueryForm
              onResult={addHistory}
              prefill={prefill}
              datalistId="distance-bays-page"
            />
          </div>
        </div>

        {/* Search history */}
        <div className="flex-1 min-h-0 flex flex-col bg-white rounded-lg shadow border border-slate-200">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
            <h2 className="text-sm font-semibold text-slate-700">
              Search history{history.length > 0 ? ` (${history.length})` : ""}
            </h2>
            {history.length > 0 && (
              <button
                type="button"
                onClick={clearHistory}
                className="text-xs text-red-600 hover:text-red-700"
              >
                Clear history
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto">
            {history.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-slate-400 p-6 text-center min-h-[120px]">
                No searches yet. Run a query to build your history.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {history.map((entry) => (
                  <li key={entry.id}>
                    <HistoryRow entry={entry} onReplay={handleReplay} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DistanceQueryPage;
